import { motion, AnimatePresence } from 'framer-motion';
import { X, Cookie, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useCookieConsentContext } from '@/contexts/CookieConsentContext';

export function CookieConsentBanner() {
  const { showBanner, acceptAll, rejectAll, openPreferences } = useCookieConsentContext();

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-banner-title"
          aria-describedby="cookie-banner-description"
        >
          <div className="max-w-4xl mx-auto">
            <div className="relative bg-slate-900/95 backdrop-blur-xl border border-slate-700 rounded-2xl p-6 shadow-2xl shadow-black/50">
              {/* Close button - acts as Reject All */}
              <button
                onClick={rejectAll}
                className="absolute top-4 right-4 p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                aria-label="Reject non-essential cookies and close"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex flex-col lg:flex-row gap-6 lg:items-center">
                {/* Icon */}
                <div className="hidden lg:flex flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500/20 to-red-500/20 border border-orange-500/30 items-center justify-center">
                  <Cookie className="w-7 h-7 text-orange-400" />
                </div>

                {/* Content */}
                <div className="flex-1 pr-8 lg:pr-0">
                  <h2 id="cookie-banner-title" className="flex items-center gap-2 text-lg font-semibold text-white mb-2">
                    <Cookie className="w-5 h-5 text-orange-400 lg:hidden" />
                    Cookie Preferences
                  </h2>
                  <p id="cookie-banner-description" className="text-sm text-slate-300 leading-relaxed">
                    We use essential cookies for site functionality and optional analytics cookies to understand how visitors use our site (via Google Analytics). This helps us improve our services for tradespeople in South Wales. By clicking 'Accept All' you consent to all cookies. You can manage preferences or reject non-essential cookies. See our{' '}
                    <Link 
                      to="/privacy" 
                      className="text-orange-400 hover:text-orange-300 underline underline-offset-2"
                    >
                      Privacy Policy
                    </Link>{' '}
                    for details.
                  </p>
                </div>

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
                  <Button
                    onClick={openPreferences}
                    variant="outline"
                    className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white min-h-[44px]"
                  >
                    Manage Preferences
                  </Button>
                  <Button
                    onClick={rejectAll}
                    variant="outline"
                    className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white min-h-[44px]"
                  >
                    Reject All
                  </Button>
                  <Button
                    onClick={acceptAll}
                    className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white shadow-lg shadow-orange-500/25 min-h-[44px]"
                  >
                    Accept All
                  </Button>
                </div>
              </div>

              {/* Trust indicator */}
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-700/50">
                <Shield className="w-4 h-4 text-cyan-400" />
                <span className="text-xs text-slate-400">
                  UK GDPR & PECR Compliant. Your privacy is protected.
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
